<template>
  <BaseLayout title="Course Actions">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Course Actions</h1>
      <div class="d-flex flex-column">
        <h3>Transmission Results to IS MUNI</h3>
        <ul>
          <li><strong>Transmit for all students:</strong> Send results for all students to IS MUNI.</li>
          <li><strong>Transmit for specified students:</strong> Input specific student UCOs to transmit their results.</li>
          <li>Before proceeding, there is possibility to generate and check CSV of data that will be transmitted.</li>
          <li>Both actions will overwrite all previous results in IS MUNI block.</li>
        </ul>
        <h3>Students</h3>
        <ul>
          <li><strong>Download UCOs list:</strong> Download a CSV file containing UCOs of all non-archived students in the system.</li>
        </ul>
        <h3>Semester</h3>
        <ul>
          <li><strong>End current and prepare new semester:</strong> Archives the current semester and creates a new one. Optionally copy global files, homeworks, and subtasks to the new semester.</li>
        </ul>
        <h3>Datasets</h3>
        <ul>
          <li><strong>Generate for all students:</strong> Choose to generate datasets only for students without them or regenerate for all students. This process may take some time.</li>
          <li><strong>Generate for specified students:</strong> Input one or more student UCOs to generate datasets.</li>
          <li><strong>Check datasets:</strong> Verify if all students have datasets available. Returns list of student UCOs without datasets (up to 10).</li>
          <li><strong>Download student dataset:</strong> Download the generated dataset file for a specific student.</li>
          <li><strong>Download dataset container log:</strong> Download the container log from dataset generation for specific student.</li>
          <li><strong>Delete student dataset:</strong> Delete dataset for a specific student.</li>
        </ul>
      </div>
    </div>
    
    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Transmit Results:</h5>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#transferResultsModal" :disabled="isTransferingAll">
          <i class="bi bi-people me-2"></i>
          <span v-if="isTransferingAll" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Transmit for all students
        </button>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#transferResultsForStudentByUcoModalForm" :disabled="isGeneratingStudent">
            <i class="bi bi-person-check me-2"></i>
          <span v-if="isGeneratingStudent" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Transmit for specified students
        </button>
      </div>
      <div class="card p-3 mt-3">
        <h5 class="card-title">Students:</h5>
        <button @click="downloadStudentsUcos" class="btn btn-success mb-2" :disabled="isDownloadingUcos">
          <span v-if="isDownloadingUcos" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-download me-2"></i>
          Download UCOs list
        </button>
      </div>
      <div class="card p-3 mt-3">
        <h5 class="card-title">Semestr:</h5>
        <button class="btn btn-warning" data-bs-toggle="modal" data-bs-target="#prepareNewSemesterModalForm">
          <i class="bi bi-exclamation-triangle me-2"></i>
          End current and prepare new semester
        </button>
      </div>
      <div class="card p-3 mt-3">
        <h5 class="card-title">Datasets:</h5>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#generateAllDatasetsModalForm" :disabled="isGeneratingAll">
          <i class="bi bi-people me-2"></i>
          <span v-if="isGeneratingAll" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Generate for all students
        </button>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm" :disabled="isGeneratingStudent" @click="setModalAction('generate')">
            <i class="bi bi-person me-2"></i>
          <span v-if="isGeneratingStudent" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Generate for specified students
        </button>
        <button @click="checkDatasetsForAll" class="btn btn-primary mb-2" :disabled="isChecking">
          <i class="bi bi-check-circle me-2"></i>
          <span v-if="isChecking" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Check datasets of all students
        </button>
        <hr>
        <button class="btn btn-success mb-2" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm" :disabled="isDownloading" @click="setModalAction('download')">
          <i class="bi bi-download me-2"></i>
          <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Download student dataset
        </button>
        <button class="btn btn-success" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm" :disabled="isDownloadingLog" @click="setModalAction('downloadLog')">
          <i class="bi bi-file-earmark-text me-2"></i>
          <span v-if="isDownloadingLog" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Download dataset container log
        </button>
        <hr>
        <button class="btn btn-danger" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm" :disabled="isDeleting" @click="setModalAction('delete')">
          <i class="bi bi-trash me-2"></i>
          <span v-if="isDeleting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Delete student dataset
        </button>
      </div>
    </template>
    
    <!-- Modal for student UCO selection (multiple purposes) -->
    <ChooseStudentByUcoModalForm
      :title="modalTitle"
      :allowMultiple="modalAllowMultiple"
      ref="ChooseStudentByUcoModalForm"
      @submitForm="handleStudentUcoSubmit"
    />

    <TransferResultsForStudentByUcoModalForm
      ref="TransferResultsForStudentByUcoModalForm"
      @submitForm="transferResultsForStudent"
    />

    <!-- Modal for transferring results -->
    <TransferResultsModal
      ref="transferResultsModal"
    />

    <StemErrorModal ref="stemErrorModal" />
    <PrepareNewSemesterModalForm
      ref="prepareNewSemesterModalForm"
      @submitForm="prepareNewSemester"
    />
    <GenerateAllDatasetsModalForm
      ref="generateAllDatasetsModalForm"
      @submitForm="generateDatasetsForAllStudents"
    />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import ChooseStudentByUcoModalForm from '@/components/ChooseStudentByUcoModalForm.vue';
import TransferResultsForStudentByUcoModalForm from '@/components/TransferResultsForStudentByUcoModalForm.vue';
import TransferResultsModal from '@/components/TransferResultsModal.vue';
import DatasetController from '@/controllers/DatasetController';
import SemesterController from '@/controllers/SemesterController';
import StudentController from '@/controllers/StudentController';
import HomeworkController from '@/controllers/HomeworkController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import PrepareNewSemesterModalForm from '@/components/PrepareNewSemesterModalForm.vue';
import GenerateAllDatasetsModalForm from '@/components/GenerateAllDatasetsModalForm.vue';
import { Modal } from 'bootstrap';
import { mapGetters } from 'vuex';

export default {
  name: 'DatasetView',
  components: {
    BaseLayout,
    ChooseStudentByUcoModalForm,
    TransferResultsForStudentByUcoModalForm,
    TransferResultsModal,
    StemErrorModal,
    PrepareNewSemesterModalForm,
    GenerateAllDatasetsModalForm
  },
  data() {
    return {
      isGeneratingStudent: false,
      isGeneratingAll: false,
      isChecking: false,
      isTransferingStudent: false,
      isTransferingAll: false,
      isGeneratingCSV: false,
      isDownloading: false,
      isDownloadingLog: false,
      isDownloadingUcos: false,
      isDeleting: false,
      modalAction: null,
      modalTitle: 'Enter Student UCO',
      modalAllowMultiple: false,
    };
  },
  computed: {
    ...mapGetters(['isTeacher']),
  },
  async created() {
    // Check if the user is a teacher
    if (!this.isTeacher) {
      this.$store.dispatch('triggerAlert', {
        type: 'error',
        title: 'Error',
        message: 'You are not authorized to access this page.',
      });
      this.$router.push('/');
    }
  },
  methods: {
    async generateDatasetsForAllStudents(regenerateAll) {
      try {
        this.isGeneratingAll = true;
        await DatasetController.generateDatasetsForAllStudents(regenerateAll);
        
        const actionText = regenerateAll 
          ? 'regenerate datasets for all students' 
          : 'generate datasets for students without datasets';
        
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Successfully sent',
          message: `Tasks to ${actionText}`,
        });
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isGeneratingAll = false;
      }
    },
    async generateDatasetForStudent(stringOfStudentUcos) {
      try {
        this.isGeneratingStudent = true;
        const listOfStudentUcos = this.checkAndSplitUcos(stringOfStudentUcos);
        if (listOfStudentUcos === null) {
          return;
        }
        await DatasetController.generateDatasetsForStudentsUcos(listOfStudentUcos);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Successfully sent',
          message: 'Task to generate datasets for students',
        });
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isGeneratingStudent = false;
      }
    },
    async checkDatasetsForAll() {
      try {
        this.isChecking = true;
        const result = await DatasetController.checkAllStudentsHaveDatasets();
        
        if (result.all_have_datasets) {
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'All students have datasets.',
          });
        } else {
          const studentsWithoutDatasets = result.students_without_datasets;
          let message = '';
          
          if (studentsWithoutDatasets.length > 10) {
            message = `More than 10 students don't have datasets (${studentsWithoutDatasets.length} students in total).`;
          } else {
            message = `The following students don't have datasets: ${studentsWithoutDatasets.join(', ')}`;
          }
          
          this.$store.dispatch('triggerAlert', {
            type: 'warning',
            title: 'Warning',
            message: message,
          });
        }
      }
      catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isChecking = false;
      }
    },
    async prepareNewSemester(payload) {
      try {
        const body = {
          name: payload.name,
          start_date: payload.start_date,
          end_date: payload.end_date,
          copy_resources_from_last_semester: payload.copy_resources_from_last_semester,
          archived: false,
        };
        // Remember current semester to detect change later
        const currentSemesterId = this.$store.getters.semesterId ?? '';
        window.sessionStorage.setItem('prevSemesterId', String(currentSemesterId));
        // Navigate to the dedicated preparing page
        this.$router.push('/preparing-new-semester');
        // Fire and await the backend action
        await SemesterController.prepareNewSemester(body);
        // When backend call resolves, trigger a token refresh so claims update
        await this.$store.dispatch('refreshToken');
        // Preparing view will detect updated semester and redirect to home
      } catch (error) {
        // If something fails, show error and return to actions page
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to prepare a new semester.'
        });
        this.$router.replace('/course_actions');
      }
    },
    async transferResultsForStudent(studentUcos) {
      try {
        this.isTransferingStudent = true;
        const listOfStudentUcos = this.checkAndSplitUcos(studentUcos);
        if (listOfStudentUcos === null) {
          return;
        }
        if (confirm(
            'This action overwrite results of specified students in IS MUNI block. \n' +
            'Do you want to proceed?'
        )) {
          const response = await StudentController.transferResultsForStudents(listOfStudentUcos);
          
          // Manually hide the modal on success
          const modalElement = document.getElementById('transferResultsForStudentByUcoModalForm');
          const modalInstance = Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
  
          if (response && response.length > 0) {
            // If inresponse are all student ucos, show error message
            if (response.length === studentUcos.length) {
              this.$store.dispatch('triggerAlert', {
                type: 'error',
                title: 'Error',
                message: 'Data transmit did not start. All students not found.'
              });
            } else if (response.length > 0) {
              // Show warning message with not found students
              this.$store.dispatch('triggerAlert', {
                type: 'warning',
                title: 'Warning',
                message: 'Data transmit not completed for students: ' + response.join(', ')
              });
            }
          } else {
            this.$store.dispatch('triggerAlert', {
              type: 'success',
              title: 'Success',
              message: 'Data transmit completed successfully.'
            });
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isTransferingStudent = false;
      }
    },
    checkAndSplitUcos(stringOfStudentUcos) {
      // Split string of student ucos by comma and trim spaces
      var listOfStudentUcos = stringOfStudentUcos.split(',').map(uco => uco.trim());      
      return listOfStudentUcos;
    },
    setModalAction(action) {
      this.modalAction = action;
      if (action === 'generate') {
        this.modalTitle = 'Choose student(s) to generate dataset';
        this.modalAllowMultiple = true;
      } else if (action === 'download') {
        this.modalTitle = 'Choose student to download dataset';
        this.modalAllowMultiple = false;
      } else if (action === 'downloadLog') {
        this.modalTitle = 'Choose student to download container log';
        this.modalAllowMultiple = false;
      } else if (action === 'delete') {
        this.modalTitle = 'Choose student to delete dataset';
        this.modalAllowMultiple = false;
      }
    },
    handleStudentUcoSubmit(studentUco) {
      if (this.modalAction === 'generate') {
        this.generateDatasetForStudent(studentUco);
      } else if (this.modalAction === 'download') {
        this.downloadDatasetForStudent(studentUco);
      } else if (this.modalAction === 'downloadLog') {
        this.downloadContainerLogForStudent(studentUco);
      } else if (this.modalAction === 'delete') {
        this.deleteDatasetForStudent(studentUco);
      }
    },
    async downloadDatasetForStudent(studentUco) {
      try {
        this.isDownloading = true;

        // Get dataset data by student UCO
        const dataset = await this.getDatasetDataByStudentUco(studentUco);
        if (!dataset) {
          return;
        }
        if (dataset && dataset.file_path) {
            await this.downloadFile(dataset.file_path);
        }
        else {
          this.$store.dispatch('triggerAlert', {
            title: 'Error',
            message: 'No dataset found.',
            type: 'error',
          });
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloading = false;
      }
    },
    async downloadContainerLogForStudent(studentUco) {
      try {
        this.isDownloadingLog = true;
        
        // Get dataset data by student UCO
        const dataset = await this.getDatasetDataByStudentUco(studentUco);
        if (!dataset) {
          return;
        }
        if (dataset && dataset.file_path) {
          await this.downloadFile(dataset.container_log_path);
        }
        else {
          this.$store.dispatch('triggerAlert', {
            title: 'Error',
            message: 'No dataset found, so no container log found.',
            type: 'error',
          });
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloadingLog = false;
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await HomeworkController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
    async getDatasetDataByStudentUco(studentUco) {
      let dataset = null;
      // Get student data
      const student = await StudentController.fetchStudentByUCO(studentUco);
      if (!student || !student.id) {
        this.$refs.stemErrorModal.show('Error', 'Student not found.');
        return null;
      }
      // Get dataset by using student Id
      dataset = await DatasetController.getDatasetForStudent(student.id);
      if (!dataset || !dataset.file_path) {
        this.$refs.stemErrorModal.show('Error', 'No dataset found for this student.');
        return null;
      }
      return dataset;
    },
    async deleteDatasetForStudent(studentUco) {
      try {
        this.isDeleting = true;

        // Get dataset data by student UCO
        const dataset = await this.getDatasetDataByStudentUco(studentUco);
        if (!dataset) {
          return;
        }

        if (confirm(`Are you sure you want to delete the dataset for student ${studentUco}?`)) {
          await DatasetController.deleteDataset(dataset.id);
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Dataset deleted successfully.',
          });
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDeleting = false;
      }
    },
    async downloadStudentsUcos() {
      try {
        this.isDownloadingUcos = true;
        const response = await StudentController.downloadStudentsUcos();
        
        // Create download link
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'student_ucos.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloadingUcos = false;
      }
    }
  }
};
</script>
